import { Box, Link, Typography } from "@mui/material";
import { Check } from "@mui/icons-material";
import { EMAIL_CONTACTO, EMAIL_CONTACTO_POR_EMPRESA } from "@helpers/constantes";
import { formatTime, getConfigName } from "@helpers/funciones";

/** Tonos de `.ld-verify-help` del diseño: texto en `--ld-text-2` y notas en `--ld-text-3`. */
const HELP_TEXT = "#4A5567";
const HELP_META = "#7A8497";

interface Props {
  /** `true` unos segundos después de reenviar: muestra "Código reenviado" en lugar del enlace. */
  justResent: boolean;
  secondsRemaining: number;
  onResend: () => void;
  /** Bloquea "Reenviar" además de la cuenta regresiva (mientras se envía o se valida). */
  resendDisabled?: boolean;
  /** Prefijo de los `data-testid` (`${testIdPrefix}-reenviar-link` y `${testIdPrefix}-contacto-link`). */
  testIdPrefix: string;
  /** Separación con el código; la validación de teléfono usa 3 (24px) y el NIP de Buró 2 (16px). */
  mt?: number;
}

/**
 * Ayuda debajo de las casillas de un código por SMS (`.ld-verify-help` del diseño): reenviar con cuenta
 * regresiva, vigencia del código y correo de contacto si nunca llega. La comparten la validación de teléfono
 * y el NIP de Buró.
 */
export const TypographyOtpHelp = ({
  justResent,
  secondsRemaining,
  onResend,
  resendDisabled = false,
  testIdPrefix,
  mt = 3,
}: Props) => {
  const email = EMAIL_CONTACTO_POR_EMPRESA[String(getConfigName(window.location.href))] ?? EMAIL_CONTACTO;

  return (
    <Box sx={{ mt, display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography component="div" sx={{ fontSize: 13, color: HELP_TEXT }}>
        ¿No recibiste el código?{" "}
        {justResent ? (
          <Typography
            component="span"
            sx={{
              fontSize: "inherit",
              color: "success.main",
              fontWeight: 500,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Check sx={{ fontSize: 14 }} /> Código reenviado
          </Typography>
        ) : (
          /* `.ld-verify-link` — enlace en negritas del color de marca, sin subrayado */
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={onResend}
            disabled={secondsRemaining > 0 || resendDisabled}
            data-testid={`${testIdPrefix}-reenviar-link`}
            sx={{
              fontFamily: "inherit",
              fontSize: "inherit",
              fontWeight: 600,
              color: "primary.main",
              verticalAlign: "baseline",
              "&:disabled": { color: HELP_META, cursor: "default", textDecoration: "none" },
            }}
          >
            {secondsRemaining > 0 ? `Reenviar en ${formatTime(secondsRemaining)}` : "Reenviar código"}
          </Link>
        )}
      </Typography>
      <Typography sx={{ fontSize: 11.5, color: HELP_META }}>El código expira en 5 minutos.</Typography>
      <Typography sx={{ fontSize: 11.5, color: HELP_META, mt: 0.5 }}>
        Si nunca llega el código, escríbenos a{" "}
        <Link
          href={`mailto:${email}`}
          underline="hover"
          data-testid={`${testIdPrefix}-contacto-link`}
          sx={{ fontSize: "inherit", fontWeight: 500, color: "primary.main" }}
        >
          {email}
        </Link>{" "}
        y te ayudamos a validarlo.
      </Typography>
    </Box>
  );
};
